/**
 * Theme Store - Manages light/dark mode with persistence
 */
import { writable } from 'svelte/store';

const browser = typeof window !== 'undefined';

type Theme = 'light' | 'dark';

const THEME_KEY = 'spa-studio.theme.v1';

// Get initial theme from localStorage or default to dark
function getInitialTheme(): Theme {
  if (!browser) return 'dark';

  const stored = localStorage.getItem(THEME_KEY);
  if (stored === 'dark' || stored === 'light') {
    return stored;
  }

  // Default to dark theme (professional look)
  return 'dark';
}

// Create the store
function createThemeStore() {
  const { subscribe, set, update } = writable<Theme>(getInitialTheme());

  // Track current value to avoid race conditions
  let currentTheme: Theme = getInitialTheme();

  // Subscribe to our own updates to keep track of current value
  subscribe((value) => {
    currentTheme = value;
  });

  return {
    subscribe,
    setTheme: (theme: Theme) => {
      if (browser) {
        localStorage.setItem(THEME_KEY, theme);
        // Apply theme class to document
        if (theme === 'dark') {
          document.documentElement.classList.add('dark');
          document.documentElement.classList.remove('light');
        } else {
          document.documentElement.classList.add('light');
          document.documentElement.classList.remove('dark');
        }
      }
      set(theme);
    },
    toggle: () => {
      // Use tracked current value instead of re-reading from localStorage
      const next = currentTheme === 'light' ? 'dark' : 'light';
      if (browser) {
        localStorage.setItem(THEME_KEY, next);
        // Apply theme class to document
        if (next === 'dark') {
          document.documentElement.classList.add('dark');
          document.documentElement.classList.remove('light');
        } else {
          document.documentElement.classList.add('light');
          document.documentElement.classList.remove('dark');
        }
      }
      set(next);
    },
    init: () => {
      if (browser) {
        const theme = getInitialTheme();
        // Apply the theme class immediately
        if (theme === 'dark') {
          document.documentElement.classList.add('dark');
          document.documentElement.classList.remove('light');
        } else {
          document.documentElement.classList.add('light');
          document.documentElement.classList.remove('dark');
        }
        set(theme);
      }
    }
  };
}

export const theme = createThemeStore();
